import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { postConnection, deleteConnection } from '../../actions';
import { FetchError, IConnectionResponse } from '../../models';
import { RootState } from '../rootReducer';
import { parse, stringify } from 'query-string';

export interface IConnectionState {
    oAuthCode?: string;
    isConnecting: boolean;
    isDisconnecting: boolean;
    error?: Error;
}

export const initialState: IConnectionState = {
    isConnecting: false,
    isDisconnecting: false,
};

const name = 'connection';

const connectionSlice = createSlice({
    initialState,
    name,
    reducers: {
        setOAuthCode(state, action: PayloadAction<string>) {
            state.oAuthCode = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(postConnectionAction.pending, (state) => {
                state.isConnecting = true;
            })
            .addCase(postConnectionAction.fulfilled, (state) => {
                state.isConnecting = false;
                state.oAuthCode = undefined;
                state.error = undefined;
            })
            .addCase(postConnectionAction.rejected, (state, { payload }) => {
                state.isConnecting = false;
                state.error = payload;
            })
            .addCase(deleteConnectionAction.pending, (state) => {
                state.isDisconnecting = true;
            })
            .addCase(deleteConnectionAction.fulfilled, (state) => {
                state.isDisconnecting = false;
                state.error = undefined;
            })
            .addCase(deleteConnectionAction.rejected, (state, { payload }) => {
                state.isDisconnecting = false;
                state.error = payload;
            });
    },
});

export const postConnectionAction = createAsyncThunk<
    IConnectionResponse,
    void,
    { state: RootState; rejectValue: FetchError }
>(`${name}/postConnection`, async (_, { getState, rejectWithValue }) => {
    try {
        const state = getState();
        const installation = state.installation.installation;
        const user = state.user.user;
        const oAuthCode = state.connection.oAuthCode!;

        const queryParameters = parse(window.location.search);
        const origin = queryParameters.origin;
        if (origin) {
            // Need to delete it here otherwise it breaks validation in the backend
            delete queryParameters.origin;
        }

        const response = await postConnection(user, installation, {
            code: oAuthCode,
            query: stringify(queryParameters),
        });

        return response;
    } catch (error) {
        if (error instanceof FetchError) {
            // api response errors i.e. 400 and 404
            return rejectWithValue({ name: error.name, message: error.message, code: error.code, body: error.body });
        }
        return rejectWithValue(error);
    }
});

export const deleteConnectionAction = createAsyncThunk<void, void, { state: RootState; rejectValue: FetchError }>(
    `${name}/deleteConnection`,
    async (_, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const installation = state.installation.installation;
            const user = state.user.user;

            return await deleteConnection(user, installation);
        } catch (error) {
            if (error instanceof FetchError) {
                // api response errors i.e. 400 and 404
                return rejectWithValue({
                    name: error.name,
                    message: error.message,
                    code: error.code,
                    body: error.body,
                });
            }
            return rejectWithValue(error);
        }
    }
);

export const { setOAuthCode } = connectionSlice.actions;

export const connectionSelectors = {
    oAuthCode: (state: RootState) => state.connection.oAuthCode,
    isConnecting: (state: RootState) => state.connection.isConnecting,
    isDisconnecting: (state: RootState) => state.connection.isDisconnecting,
    error: (state: RootState) => state.connection.error,
};

export default connectionSlice.reducer;
